import React, { useState } from 'react';
import { X, Clock, CheckCircle } from '@phosphor-icons/react';
import { workerFetch } from '../utils/apiUtils';

const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

const RoutineTimeModal = ({ isOpen, onClose, productName, timeOfDay }) => {
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const timeOptions = [
    '06:00', '06:30', '07:00', '07:30', '08:00', '08:30', '09:00', '09:30',
    '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
    '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30',
    '22:00', '22:30', '23:00', '23:30'
  ];

  const getTimeOfDayText = (time) => {
    switch (time) {
      case 'morning': return 'صبح';
      case 'afternoon': return 'ظهر';
      case 'evening': return 'عصر';
      case 'night': return 'شب';
      default: return 'روز';
    }
  };

  const handleSaveTime = async () => {
    if (!selectedTime) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('firebaseIdToken');
      const response = await workerFetch(`${WORKER_URL}/api/routine/set-time`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          timeOfDay,
          productName,
          time: selectedTime
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          setSelectedTime('');
        }, 2000);
      } else {
        console.error('Failed to save routine time');
      }
    } catch (error) {
      console.error('Error saving routine time:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Clock size={24} weight="bold" />
              </div>
              <div>
                <h2 className="text-xl font-bold">تنظیم زمان روتین</h2>
                <p className="text-purple-100 text-sm">{productName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" weight="bold" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">زمان تنظیم شد!</h3>
              <p className="text-gray-600">
                {productName} برای {getTimeOfDayText(timeOfDay)} در ساعت {selectedTime} تنظیم شد
              </p>
            </div>
          ) : (
            <>
              {/* Time Selection */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 mb-4">
                  چه ساعتی {getTimeOfDayText(timeOfDay)} {productName} را استفاده می‌کنید؟
                </h3>
                
                <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                  {timeOptions.map((time) => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedTime === time
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected Time Display */}
              {selectedTime && (
                <div className="bg-purple-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Clock size={20} className="text-purple-600" />
                    <div>
                      <p className="font-semibold text-purple-800">زمان انتخاب شده:</p>
                      <p className="text-purple-600">{selectedTime}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  انصراف
                </button>
                <button
                  onClick={handleSaveTime}
                  disabled={!selectedTime || loading}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? 'در حال ذخیره...' : 'ذخیره'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoutineTimeModal;
